import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faSimCard,
  faShieldHalved,
  faSpinner,
  faTriangleExclamation,
  faCircleCheck,
  faRightFromBracket,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import StoreHeader from '../../components/StoreHeader';
import Footer from '../../components/Footer';
import { useAuth } from '../../context/AuthContext';
import {
  getUser,
  updateProfile,
  changePassword,
  deleteAccount,
  getMyEsims,
} from './accountApi';
import {
  statusUi,
  isInactive,
  isReadyToUse,
  isStatusUnavailable,
} from './esimStatus';
import './account.css';

const formatData = (mb) => {
  if (mb == null) return '—';
  if (mb >= 1024) {
    const gb = mb / 1024;
    return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
  }
  return `${mb} MB`;
};

const formatDate = (value, locale) => {
  if (!value) return '—';
  const d = typeof value === 'number' ? new Date(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
};

const Notice = ({ message }) =>
  message ? (
    <div className="acc-notice">
      <FontAwesomeIcon icon={faCircleCheck} />
      <span>{message}</span>
    </div>
  ) : null;

const ErrorText = ({ message }) =>
  message ? <p className="acc-error">{message}</p> : null;

/* ---------------- Profil ---------------- */
const ProfileTab = ({ email }) => {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState({ loading: true });
  const [form, setForm] = useState({ fullName: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getUser(email)
      .then((user) => {
        if (cancelled) return;
        setState({ user });
        setForm({ fullName: user?.fullName || '', phone: user?.phone || '' });
      })
      .catch((err) => !cancelled && setState({ error: err.message }));
    return () => {
      cancelled = true;
    };
  }, [email]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateProfile({ email, fullName: form.fullName.trim(), phone: form.phone.trim() });
      setSaved(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (state.loading) {
    return <div className="acc-state"><FontAwesomeIcon icon={faSpinner} spin size="2x" /></div>;
  }
  if (state.error) {
    return (
      <div className="acc-state">
        <FontAwesomeIcon icon={faTriangleExclamation} size="2x" />
        <p>{state.error}</p>
      </div>
    );
  }

  return (
    <div className="acc-card">
      <h2 className="acc-card-title">{t('account.profile.title')}</h2>
      <form className="acc-form" onSubmit={submit}>
        <Notice message={saved ? t('account.saved') : null} />
        <ErrorText message={error} />

        <div className="acc-field">
          <label htmlFor="fullName">{t('auth.field.fullName')}</label>
          <input
            id="fullName"
            value={form.fullName}
            onChange={(e) => setForm({ ...form, fullName: e.target.value })}
          />
        </div>

        <div className="acc-field">
          <label htmlFor="email">{t('auth.field.email')}</label>
          <input id="email" value={state.user?.email || email || ''} disabled />
        </div>

        <div className="acc-field">
          <label htmlFor="phone">{t('account.field.phone')}</label>
          <input
            id="phone"
            type="tel"
            placeholder={t('account.field.phone.ph')}
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </div>

        {state.user?.createdDate && (
          <p className="acc-meta">
            {t('account.memberSince')}: {formatDate(state.user.createdDate, i18n.language)}
          </p>
        )}

        <button type="submit" className="acc-submit" disabled={saving}>
          {saving ? t('account.saving') : t('account.save')}
        </button>
      </form>
    </div>
  );
};

/* ---------------- eSIM'lerim ---------------- */
const EsimCard = ({ esim }) => {
  const { t, i18n } = useTranslation();
  const ui = statusUi(esim);
  const ready = isReadyToUse(esim);
  const unavailable = isStatusUnavailable(esim);
  const total = esim.totalDataMb;
  const remaining = esim.remainingDataMb;
  // READY_TO_USE'da sayaçlar 0, STATUS_UNAVAILABLE'da null gelir — ikisinde de
  // veri çubuğunu gizle, açıklayıcı bir ipucu göster.
  const showData = !ready && !unavailable && total != null && total > 0;
  const pct =
    showData && remaining != null
      ? Math.max(0, Math.min(100, (remaining / total) * 100))
      : null;

  return (
    <Link
      to={`/account/esim/${esim.orderId}`}
      state={{ esim }}
      className="esim-order"
    >
      <div className="esim-order-head">
        <span className="esim-order-name">
          {esim.providerBundleName || esim.clientProductId}
        </span>
        <span className={`esim-order-status status--${ui.cls}`}>{t(ui.key)}</span>
        <FontAwesomeIcon icon={faChevronRight} className="esim-order-chevron" />
      </div>

      <div className="esim-order-rows">
        {showData && (
          <div className="esim-order-data">
            <div className="esim-order-data-label">
              <span>{t('account.esims.data')}</span>
              <span>{formatData(remaining)} / {formatData(total)}</span>
            </div>
            {pct != null && (
              <div className="esim-order-bar">
                <div className="esim-order-bar-fill" style={{ width: `${pct}%` }} />
              </div>
            )}
          </div>
        )}
        {ready && (
          <div className="esim-order-hint">{t('account.esims.readyHint')}</div>
        )}
        {unavailable && (
          <div className="esim-order-hint esim-order-hint--warn">
            {t('account.esims.unavailableHint')}
          </div>
        )}
        {showData && esim.esimExpiresAt && (
          <div className="esim-order-meta">
            <span>{t('account.esims.expires')}</span>
            <span>{formatDate(esim.esimExpiresAt, i18n.language)}</span>
          </div>
        )}
      </div>
    </Link>
  );
};

const EsimsTab = () => {
  const { t } = useTranslation();
  const [state, setState] = useState({ loading: true });
  const [view, setView] = useState('active');

  useEffect(() => {
    let cancelled = false;
    getMyEsims()
      .then((data) => !cancelled && setState({ data }))
      .catch((err) => !cancelled && setState({ error: err.message }));
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.loading) {
    return <div className="acc-state"><FontAwesomeIcon icon={faSpinner} spin size="2x" /></div>;
  }
  if (state.error) {
    return (
      <div className="acc-state">
        <FontAwesomeIcon icon={faTriangleExclamation} size="2x" />
        <p>{state.error}</p>
      </div>
    );
  }
  if (!state.data?.length) {
    return (
      <div className="acc-state">
        <FontAwesomeIcon icon={faSimCard} size="2x" />
        <p>{t('account.esims.empty')}</p>
        <Link to="/" className="acc-submit acc-submit--inline">{t('account.esims.emptyCta')}</Link>
      </div>
    );
  }

  // Sekme ataması yalnızca displayStatus'a göre (bkz. esimStatus.js).
  const active = state.data.filter((e) => !isInactive(e));
  const passive = state.data.filter((e) => isInactive(e));
  const list = view === 'active' ? active : passive;

  return (
    <div className="esim-tab">
      <div className="esim-subtabs">
        <button
          type="button"
          className={`esim-subtab ${view === 'active' ? 'active' : ''}`}
          onClick={() => setView('active')}
        >
          {t('account.esims.active')} ({active.length})
        </button>
        <button
          type="button"
          className={`esim-subtab ${view === 'passive' ? 'active' : ''}`}
          onClick={() => setView('passive')}
        >
          {t('account.esims.passive')} ({passive.length})
        </button>
      </div>

      {list.length ? (
        <div className="esim-orders">
          {list.map((esim) => (
            <EsimCard key={esim.orderId} esim={esim} />
          ))}
        </div>
      ) : (
        <p className="esim-subtab-empty">
          {view === 'active' ? t('account.esims.noActive') : t('account.esims.noPassive')}
        </p>
      )}
    </div>
  );
};

/* ---------------- Güvenlik ---------------- */
const SecurityTab = ({ email }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const [form, setForm] = useState({ oldPassword: '', newPassword: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setDone(false);
    if (!form.oldPassword || !form.newPassword) {
      setError(t('auth.error.required'));
      return;
    }
    if (form.newPassword !== form.confirm) {
      setError(t('account.error.passwordMismatch'));
      return;
    }
    setBusy(true);
    try {
      await changePassword({ email, oldPassword: form.oldPassword, newPassword: form.newPassword });
      setDone(true);
      setForm({ oldPassword: '', newPassword: '', confirm: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await deleteAccount(email);
      logout();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="acc-stack">
      <div className="acc-card">
        <h2 className="acc-card-title">{t('account.security.title')}</h2>
        <form className="acc-form" onSubmit={submit}>
          <Notice message={done ? t('account.passwordChanged') : null} />
          <ErrorText message={error} />
          <div className="acc-field">
            <label htmlFor="oldPassword">{t('account.field.oldPassword')}</label>
            <input
              id="oldPassword"
              type="password"
              autoComplete="current-password"
              value={form.oldPassword}
              onChange={(e) => setForm({ ...form, oldPassword: e.target.value })}
            />
          </div>
          <div className="acc-field">
            <label htmlFor="newPassword">{t('account.field.newPassword')}</label>
            <input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              value={form.newPassword}
              onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
            />
          </div>
          <div className="acc-field">
            <label htmlFor="confirm">{t('account.field.confirmPassword')}</label>
            <input
              id="confirm"
              type="password"
              autoComplete="new-password"
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            />
          </div>
          <button type="submit" className="acc-submit" disabled={busy}>
            {busy ? t('account.saving') : t('account.changePassword')}
          </button>
        </form>
      </div>

      <div className="acc-card acc-danger">
        <h2 className="acc-card-title">{t('account.danger.title')}</h2>
        <button type="button" className="acc-logout" onClick={logout}>
          <FontAwesomeIcon icon={faRightFromBracket} />
          {t('nav.logout')}
        </button>

        {!confirmDelete ? (
          <button type="button" className="acc-delete" onClick={() => setConfirmDelete(true)}>
            {t('account.delete')}
          </button>
        ) : (
          <div className="acc-delete-confirm">
            <p>{t('account.delete.confirm')}</p>
            <div className="acc-delete-actions">
              <button type="button" className="acc-cancel" onClick={() => setConfirmDelete(false)}>
                {t('account.cancel')}
              </button>
              <button type="button" className="acc-delete" onClick={doDelete} disabled={deleting}>
                {deleting ? t('account.saving') : t('account.delete.confirmBtn')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------------- Sayfa ---------------- */
const TABS = [
  { key: 'profile', icon: faUser, labelKey: 'account.tab.profile' },
  { key: 'esims', icon: faSimCard, labelKey: 'account.tab.esims' },
  { key: 'security', icon: faShieldHalved, labelKey: 'account.tab.security' },
];

const Account = () => {
  const { t } = useTranslation();
  const { email } = useAuth();
  const [tab, setTab] = useState('profile');

  return (
    <div className="account">
      <StoreHeader />
      <main className="account-main">
        <h1 className="account-title">{t('account.title')}</h1>

        <div className="account-layout">
          <nav className="account-tabs">
            {TABS.map((tb) => (
              <button
                key={tb.key}
                className={`account-tab ${tab === tb.key ? 'active' : ''}`}
                onClick={() => setTab(tb.key)}
              >
                <FontAwesomeIcon icon={tb.icon} />
                {t(tb.labelKey)}
              </button>
            ))}
          </nav>

          <div className="account-panel">
            {tab === 'profile' && <ProfileTab email={email} />}
            {tab === 'esims' && <EsimsTab />}
            {tab === 'security' && <SecurityTab email={email} />}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Account;
